import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { ChakraProvider } from "@chakra-ui/react";
import Loader from "components/Loader";
import { buildTheme } from "lib/tenant/buildTheme";
import {
  DEFAULT_TENANT_CONFIG,
  TenantConfig,
} from "lib/tenant/types";
import { fetchTenantConfig } from "services/api/tenantResolver";

type TenantContextValue = {
  tenant: TenantConfig;
  primaryColor: string;
  secondaryColor: string;
};

const TenantContext = createContext<TenantContextValue>({
  tenant: DEFAULT_TENANT_CONFIG,
  primaryColor: DEFAULT_TENANT_CONFIG.primaryColor,
  secondaryColor: DEFAULT_TENANT_CONFIG.secondaryColor,
});

export function useTenant() {
  return useContext(TenantContext);
}

type TenantProviderProps = {
  children: ReactNode;
};

function setLinkHref(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMetaContent(name: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);

  useEffect(() => {
    fetchTenantConfig().then(setTenant);
  }, []);

  useEffect(() => {
    if (!tenant) {
      return;
    }

    document.title = `${tenant.name} Subscriptions`;
    setMetaContent("description", `Subscribe | ${tenant.name}`);

    if (!tenant.favicon) {
      return;
    }
    setLinkHref("icon", tenant.favicon);
    setLinkHref("apple-touch-icon", tenant.favicon);
  }, [tenant]);

  const theme = useMemo(() => {
    if (!tenant) {
      return buildTheme(
        DEFAULT_TENANT_CONFIG.primaryColor,
        DEFAULT_TENANT_CONFIG.secondaryColor
      );
    }

    return buildTheme(tenant.primaryColor, tenant.secondaryColor);
  }, [tenant]);

  if (!tenant) {
    return <Loader />;
  }

  return (
    <TenantContext.Provider
      value={{
        tenant,
        primaryColor: tenant.primaryColor,
        secondaryColor: tenant.secondaryColor,
      }}
    >
      <ChakraProvider theme={theme}>{children}</ChakraProvider>
    </TenantContext.Provider>
  );
}
